import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, RefreshCw, CheckCircle2, XCircle, Loader2, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getPlatformInfo } from '../services/platformDetection';
import { linkOneSignalForAndroid, resetAndroidLinker, pushUserIdToAndroidNative } from '../utils/oneSignalAndroidLinker';
import { initOneSignalWeb, requestOneSignalPermission, getOneSignalState } from '../utils/oneSignalWeb';

import { useSafeBack } from '@/utils/useSafeBack';
/**
 * Push Diagnostics page — accessible from any device at `/diagnostics/push`.
 *
 * Renders ALL the information needed to diagnose why push notifications
 * are or aren't reaching the current device, without requiring DevTools.
 * Designed to be opened on the actual Android/iOS native app where the
 * issue manifests.
 *
 * Sources of truth shown:
 *   • Platform detection result (web / android / ios) + device model + UA
 *   • Whether the OneSignal Web SDK is loaded
 *   • Whether direct globals / native bridges are exposed
 *   • Linker outcome (`window.__eili_push_diag__`) — was a player_id
 *     discovered, by which mechanism, and did the server-side link succeed
 *   • Backend `/api/notifications/push-status` — whether we have any
 *     stored subscription (VAPID or OneSignal)
 *   • Backend `/api/notifications/reregister-onesignal` — whether the
 *     OneSignal REST API actually sees this user's external_id with
 *     ≥ 1 subscription
 *
 * Action buttons:
 *   • Re-run linker (resets internal guard, fires another discovery pass)
 *   • Send test push (calls the existing /test-push endpoint)
 */

const API_BASE = `${window.location.origin}/api`;

function StatusRow({ label, value, ok = null, mono = false }) {
  let icon = null;
  if (ok === true) icon = <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />;
  if (ok === false) icon = <XCircle className="h-4 w-4 text-rose-400 shrink-0" />;
  return (
    <div className="flex items-start gap-3 py-2 border-b border-white/5">
      <div className="text-xs text-slate-400 w-44 shrink-0">{label}</div>
      <div className="flex items-start gap-2 flex-1 min-w-0">
        {icon}
        <div className={`text-xs flex-1 break-all ${mono ? 'font-mono' : ''}`} style={{ color: 'var(--theme-text)' }}>
          {value === null || value === undefined ? '—' : typeof value === 'boolean' ? String(value) : value}
        </div>
      </div>
    </div>
  );
}

export default function PushDiagnosticsPage() {
  const navigate = useNavigate();
  const goBackSafe = useSafeBack('/settings');
  const [diag, setDiag] = useState(null);
  const [statusRes, setStatusRes] = useState(null);
  const [remoteRes, setRemoteRes] = useState(null);
  const [linkerDiag, setLinkerDiag] = useState(null);
  const [osWebState, setOsWebState] = useState(null);
  const [osWebDiag, setOsWebDiag] = useState(null);
  const [inspectData, setInspectData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reLinking, setReLinking] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    setDiag(getPlatformInfo());
    setLinkerDiag(window.__eili_push_diag__ || null);
    setOsWebDiag(window.__eili_onesignal_diag__ || null);
    try {
      const [s, r, w, ins] = await Promise.allSettled([
        axios.get(`${API_BASE}/notifications/push-status`, { withCredentials: true }),
        axios.post(`${API_BASE}/notifications/reregister-onesignal`, {}, { withCredentials: true }),
        getOneSignalState(),
        axios.get(`${API_BASE}/notifications/inspect-user-push-state`, { withCredentials: true }),
      ]);
      if (s.status === 'fulfilled') setStatusRes(s.value.data);
      if (r.status === 'fulfilled') setRemoteRes(r.value.data);
      if (w.status === 'fulfilled') setOsWebState(w.value);
      if (ins.status === 'fulfilled') setInspectData(ins.value.data);
    } catch {
      // ignore — page still useful for the offline checks
    }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const handleReLink = async () => {
    setReLinking(true);
    try {
      const me = await axios.get(`${API_BASE}/auth/me`, { withCredentials: true });
      const userId = me.data?.user_id;
      if (userId) {
        resetAndroidLinker();
        // Try the official AndroidNative.setUserId bridge first — when
        // present, native code calls OneSignal.login(uid) directly and
        // the discovery loop short-circuits.
        pushUserIdToAndroidNative(userId);
        await linkOneSignalForAndroid(userId, API_BASE);
        // Also re-init the Web SDK on the off-chance it failed earlier.
        await initOneSignalWeb();
        await new Promise((r) => setTimeout(r, 2000));
      }
    } catch { /* ignore */ }
    setLinkerDiag(window.__eili_push_diag__ || null);
    setOsWebDiag(window.__eili_onesignal_diag__ || null);
    setReLinking(false);
    refresh();
  };

  const handleRequestPermission = async () => {
    setRequesting(true);
    try {
      const result = await requestOneSignalPermission();
      setTestResult({ sent: false, message: `Permission: ${result}` });
    } catch (e) {
      setTestResult({ sent: false, message: `Permission request failed: ${e?.message || e}` });
    }
    setRequesting(false);
    refresh();
  };

  const handleTestPush = async () => {
    setSending(true);
    setTestResult(null);
    try {
      const r = await axios.post(`${API_BASE}/notifications/test-push`, {}, { withCredentials: true });
      setTestResult(r.data);
    } catch (e) {
      setTestResult({ sent: false, message: e?.response?.data?.detail || String(e) });
    }
    setSending(false);
  };

  const ua = navigator.userAgent;
  const hasSdkV16 = !!window.OneSignal?.User;
  const hasSdkV15 = typeof window.OneSignal?.getUserId === 'function';
  const hasDeferred = Array.isArray(window.OneSignalDeferred);
  const hasDirectGlobal = !!(window.onesignalplayerid || window.OneSignalPlayerId || window.__ONESIGNAL_PLAYER_ID__);
  const hasBridge = !!(
    window.AndroidBridge || window.Android ||
    window.NativeBridge || window.EiliNativeBridge ||
    window.NativePaymentBridge || window.OneSignalBridge
  );
  const hasAndroidNative = !!(window.AndroidNative && typeof window.AndroidNative.setUserId === 'function');
  const remoteFound = remoteRes?.onesignal_linked && (remoteRes?.subscription_count ?? 0) > 0;

  return (
    <div className="min-h-screen px-4 pb-24 pt-4" style={{ background: 'var(--theme-bg)', color: 'var(--theme-text)' }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={goBackSafe} className="p-2 rounded-full hover:bg-white/5" data-testid="push-diag-back">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-lg font-semibold">Push diagnostics</h1>
          <button onClick={refresh} disabled={loading} className="ml-auto p-2 rounded-full hover:bg-white/5" data-testid="push-diag-refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        <p className="text-xs mb-4" style={{ color: 'var(--theme-textSecondary)' }}>
          Open this page on your phone to see exactly why notifications are or aren't reaching it. The page checks the device, the linker, and the server.
        </p>

        {/* Device */}
        <Section title="Device">
          <StatusRow label="Detected platform" value={diag?.platform} ok={!!diag?.platform} />
          <StatusRow label="Device model" value={diag?.androidDeviceModel || '—'} />
          <StatusRow label="Standalone PWA" value={!!diag?.isStandalone} />
          <StatusRow label="iOS standalone flag" value={!!diag?.iosStandalone} />
          <StatusRow label="Despia runtime" value={diag?.isDespia} ok={diag?.isDespia} />
          <StatusRow label="Android WebView (`; wv)`)" value={!!diag?.isAndroidWebView} ok={diag?.isAndroidWebView} />
          <StatusRow label="User agent" value={ua} mono />
        </Section>

        {/* Inspect data — the gold standard end-to-end picture */}
        {inspectData && (
          <Section title="🔍 End-to-end push state for THIS account">
            {Array.isArray(inspectData.verdict) && inspectData.verdict.map((v, i) => (
              <div key={i} className="text-xs p-2 rounded mb-1" style={{
                background: v.startsWith('✅') ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                color: v.startsWith('✅') ? '#10b981' : '#ef4444',
              }} data-testid={`push-diag-verdict-${i}`}>{v}</div>
            ))}
            <StatusRow label="user_id" value={inspectData.user_id} mono />
            <StatusRow label="push_enabled (resolved)" value={String(inspectData.push_enabled_resolved)} ok={inspectData.push_enabled_resolved} />
            <StatusRow label="OneSignal user found" value={String(inspectData.onesignal_lookup?.found)} ok={inspectData.onesignal_lookup?.found} />
            <StatusRow label="Deliverable subs" value={inspectData.onesignal_lookup?.deliverable_count ?? '—'} ok={(inspectData.onesignal_lookup?.deliverable_count || 0) > 0} />
            <StatusRow label="Live push delivered" value={String(inspectData.live_push_delivered)} ok={inspectData.live_push_delivered === true} />
            <details className="text-xs mt-2" style={{ color: 'var(--theme-text)' }}>
              <summary style={{ cursor: 'pointer', opacity: 0.7 }}>Show raw JSON</summary>
              <pre className="text-[10px] opacity-80 whitespace-pre-wrap mt-2">{JSON.stringify(inspectData, null, 2)}</pre>
            </details>
          </Section>
        )}

        {/* OneSignal Web SDK state */}
        <Section title="OneSignal Web SDK (browsers + PWA + Android WebView)">
          <StatusRow label="SDK initialized" value={osWebDiag?.initialized} ok={!!osWebDiag?.initialized} />
          <StatusRow label="App ID prefix" value={osWebDiag?.appIdPrefix ? `${osWebDiag.appIdPrefix}…` : '—'} mono />
          <StatusRow label="Init error" value={osWebDiag?.initError || '—'} />
          <StatusRow label="Permission" value={osWebState?.permission} ok={osWebState?.permission === 'granted'} />
          <StatusRow label="Opted in to push" value={osWebState?.optedIn} ok={osWebState?.optedIn} />
          <StatusRow label="Subscription ID" value={osWebState?.subscriptionId ? `${osWebState.subscriptionId.slice(0, 12)}…` : '—'} mono />
          <StatusRow label="Logged-in user" value={osWebDiag?.loggedInUserId || '—'} />
        </Section>

        {/* OneSignal hooks */}
        <Section title="OneSignal hooks present in this WebView">
          <StatusRow label="Web SDK v16" value={hasSdkV16} ok={hasSdkV16} />
          <StatusRow label="Web SDK v15" value={hasSdkV15} ok={hasSdkV15} />
          <StatusRow label="OneSignalDeferred queue" value={hasDeferred} ok={hasDeferred} />
          <StatusRow label="window.onesignalplayerid" value={hasDirectGlobal} ok={hasDirectGlobal} />
          <StatusRow label="JS bridge global" value={hasBridge} ok={hasBridge} />
          <StatusRow
            label="AndroidNative.setUserId (preferred)"
            value={hasAndroidNative ? 'present — official native bridge' : 'absent'}
            ok={hasAndroidNative}
          />
        </Section>

        {/* Linker */}
        <Section title="Linker discovery result">
          {linkerDiag ? (
            <>
              <StatusRow label="Outcome" value={linkerDiag.outcome} ok={linkerDiag.outcome === 'player_id_found'} />
              <StatusRow label="Source" value={linkerDiag.source || '—'} />
              <StatusRow label="Player ID" value={linkerDiag.playerIdMasked || '—'} mono />
              <StatusRow label="Server link status" value={linkerDiag.linkStatus || '—'} ok={linkerDiag.linkStatus === 'linked' || linkerDiag.linkStatus === 'ok'} />
              <StatusRow label="Server link error" value={linkerDiag.linkError || '—'} />
              <StatusRow label="Last run" value={linkerDiag.ranAt} />
            </>
          ) : (
            <p className="text-xs py-3" style={{ color: 'var(--theme-textSecondary)' }}>
              Linker hasn't run yet on this device. Tap "Re-link now" below to trigger it.
            </p>
          )}
        </Section>

        {/* Server */}
        <Section title="Server-side push state">
          <StatusRow label="Local subscription stored" value={statusRes?.subscribed ? `yes (${statusRes.type})` : 'no'} ok={!!statusRes?.subscribed} />
          <StatusRow label="OneSignal sees this user" value={remoteFound ? 'yes' : 'no'} ok={remoteFound} />
          <StatusRow label="Subscription count" value={remoteRes?.subscription_count ?? '—'} />
          <StatusRow label="OneSignal detail" value={remoteRes?.detail || '—'} />
          {Array.isArray(remoteRes?.subscriptions) && remoteRes.subscriptions.length > 0 && (
            <div className="pt-2">
              <div className="text-xs text-slate-400 mb-1">Linked devices:</div>
              {remoteRes.subscriptions.map((s, i) => (
                <div key={i} className="text-xs flex items-center gap-2 ml-2 py-0.5">
                  <span className="font-mono">{s.type}</span>
                  <span style={{ color: s.enabled ? '#10b981' : '#ef4444' }}>
                    {s.enabled ? '✓ enabled' : '✗ disabled'}
                  </span>
                  {!s.token_set && <span style={{ color: '#f59e0b' }}>(no token)</span>}
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Actions */}
        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={handleRequestPermission}
            disabled={requesting}
            className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            style={{ background: 'rgba(99,102,241,0.18)', border: '1px solid rgba(99,102,241,0.4)', color: 'var(--theme-text)' }}
            data-testid="push-diag-request-permission"
          >
            {requesting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Request push permission (OneSignal Web SDK)
          </button>
          <button
            onClick={handleReLink}
            disabled={reLinking}
            className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            style={{ background: 'var(--theme-accent)', color: '#fff' }}
            data-testid="push-diag-relink"
          >
            {reLinking ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Re-link this device now
          </button>
          <button
            onClick={handleTestPush}
            disabled={sending}
            className="w-full py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2"
            style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid var(--glass-border)', color: 'var(--theme-text)' }}
            data-testid="push-diag-test-push"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            Send a test push to me
          </button>
          {testResult && (
            <div
              className="text-xs p-3 rounded-lg mt-1"
              style={{
                background: testResult.sent ? 'rgba(16,185,129,0.12)' : 'rgba(239,68,68,0.12)',
                color: testResult.sent ? '#10b981' : '#ef4444',
              }}
            >
              {testResult.message || (testResult.sent ? 'Sent.' : 'Not sent.')}
              {testResult.diagnostics && (
                <pre className="mt-2 text-[10px] opacity-80 whitespace-pre-wrap">{JSON.stringify(testResult.diagnostics, null, 2)}</pre>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mb-4 p-4 rounded-2xl" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
      <h2 className="text-xs uppercase tracking-wider mb-2" style={{ color: 'var(--theme-textSecondary)' }}>{title}</h2>
      <div>{children}</div>
    </div>
  );
}
